import { useEffect, useState } from "react";
import { Translate } from "react-localize-redux";
import Checkbox from "../../../../utils/components/Checkbox";
import DataTable from "../../../../utils/components/DataTable";
import SelectAllHeader from "./SelectAllHeader";

const AuthorizationDetailEdit = ({
  formAuthorization,
  setFormAuthorization,
  groups,
}) => {
  const [columns, setColumns] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);

  useEffect(() => {
    const newAuthorizations = groups.map((group) => {
      const authorization = formAuthorization.find(
        (auth) => auth.group_id === group.id
      );
      return { ...group, ...authorization };
    });
    setAuthorizations(newAuthorizations);
  }, [groups, formAuthorization]);

  useEffect(() => {
    const handleChange = (id, authorization) => {
      const newAuthorizations = authorizations.map((auth) => {
        if (auth.id === id) {
          return { ...auth, ...authorization };
        }
        return auth;
      });
      setAuthorizations(newAuthorizations);
      setFormAuthorization(
        newAuthorizations.map((auth) => ({
          group_id: auth.id,
          is_access: auth.is_access || false,
          is_view_all: auth.is_view_all || false,
          is_view_received: auth.is_view_received || false,
          is_create: auth.is_create || false,
        }))
      );
    };

    const handleSelectAll = (field, checked) => {
      const newAuthorizations = authorizations.map((auth) => {
        if (field === "is_access") {
          if (auth.is_view_all || auth.is_view_received || auth.is_create)
            return auth;
        } else {
          if (auth.is_access) return auth;
        }
        return { ...auth, [field]: checked };
      });
      setAuthorizations(newAuthorizations);
      setFormAuthorization(
        newAuthorizations.map((auth) => ({
          group_id: auth.id,
          is_access: auth.is_access || false,
          is_view_all: auth.is_view_all || false,
          is_view_received: auth.is_view_received || false,
          is_create: auth.is_create || false,
        }))
      );
    };

    const total = authorizations.length;

    const eligibleForAccess = authorizations.filter(
      (auth) => !auth.is_view_all && !auth.is_view_received && !auth.is_create
    );
    const allAccessChecked =
      eligibleForAccess.length > 0 &&
      eligibleForAccess.every((auth) => auth.is_access);
    const accessSelected = authorizations.filter((a) => a.is_access).length;

    const eligibleForOthers = authorizations.filter((auth) => !auth.is_access);
    const allViewAllChecked =
      eligibleForOthers.length > 0 &&
      eligibleForOthers.every((auth) => auth.is_view_all);
    const viewAllSelected = authorizations.filter((a) => a.is_view_all).length;

    const allViewReceivedChecked =
      eligibleForOthers.length > 0 &&
      eligibleForOthers.every((auth) => auth.is_view_received);
    const viewReceivedSelected = authorizations.filter(
      (a) => a.is_view_received
    ).length;

    const allCreateChecked =
      eligibleForOthers.length > 0 &&
      eligibleForOthers.every((auth) => auth.is_create);
    const createSelected = authorizations.filter((a) => a.is_create).length;

    setColumns([
      {
        name: <Translate id="form_templates.authorization.groups" />,
        cell: (row) => (
          <>
            <strong className="me-1">{row.name}</strong> ({row.company_name})
          </>
        ),
        width: "30%",
      },
      {
        name: (
          <SelectAllHeader
            titleId="form_templates.authorization.no_access"
            checked={allAccessChecked}
            onChange={(checked) => handleSelectAll("is_access", checked)}
            selected={accessSelected}
            total={total}
            disabled={eligibleForAccess.length === 0}
          />
        ),
        selector: (row) => row.is_access,
        cell: (row) => (
          <Checkbox
            checked={row.is_access || false}
            disabled={row.is_view_all || row.is_view_received || row.is_create}
            onChange={(checked) =>
              handleChange(row.id, { is_access: checked })
            }
            size="lg"
          />
        ),
        center: true,
      },
      {
        name: (
          <SelectAllHeader
            titleId="form_templates.authorization.view_all_forms"
            checked={allViewAllChecked}
            onChange={(checked) => handleSelectAll("is_view_all", checked)}
            selected={viewAllSelected}
            total={total}
            disabled={eligibleForOthers.length === 0}
          />
        ),
        selector: (row) => row.is_view_all,
        cell: (row) => (
          <Checkbox
            checked={row.is_view_all || false}
            disabled={row.is_access}
            onChange={(checked) =>
              handleChange(row.id, { is_view_all: checked })
            }
            size="lg"
          />
        ),
        center: true,
      },
      {
        name: (
          <SelectAllHeader
            titleId="form_templates.authorization.view_received"
            checked={allViewReceivedChecked}
            onChange={(checked) => handleSelectAll("is_view_received", checked)}
            selected={viewReceivedSelected}
            total={total}
            disabled={eligibleForOthers.length === 0}
          />
        ),
        selector: (row) => row.is_view_received,
        cell: (row) => (
          <Checkbox
            checked={row.is_view_received || false}
            disabled={row.is_access}
            onChange={(checked) =>
              handleChange(row.id, { is_view_received: checked })
            }
            size="lg"
          />
        ),
        center: true,
      },
      {
        name: (
          <SelectAllHeader
            titleId="form_templates.authorization.create"
            checked={allCreateChecked}
            onChange={(checked) => handleSelectAll("is_create", checked)}
            selected={createSelected}
            total={total}
            disabled={eligibleForOthers.length === 0}
          />
        ),
        selector: (row) => row.is_create,
        cell: (row) => (
          <Checkbox
            checked={row.is_create || false}
            disabled={row.is_access}
            onChange={(checked) =>
              handleChange(row.id, { is_create: checked })
            }
            size="lg"
          />
        ),
        center: true,
      },
    ]);
  }, [authorizations, setColumns, setFormAuthorization]);

  return (
    <div className="AuthorizationPage py-5">
      <DataTable data={authorizations} columns={columns} />
    </div>
  );
};
export default AuthorizationDetailEdit;
